import { GameState, RoundType } from 'game-logic';
import { Card } from '../logic/card/card';
import { UndoCommand } from '../logic/commands/undo-command';
import { PossibleMove } from '../logic/possible-moves-finder';
import {
  OnBoardChangedAction,
  OnCommandCommittedAction,
  OnScoreChangedAction,
  SolitaireGameLogic,
} from '../logic/solitaire-game-logic';
import { GameRound } from 'src/games/common/phaser/model/game-round';
import { RoundResultSubmitter } from 'src/games/common/phaser/model/round-result-submitter';

export type OnGameStateChangedAction = (state: GameState) => void;

export class SolitaireGameRound implements GameRound {
  public get roundType(): RoundType {
    return RoundType.Standard;
  }
  public get currentGame(): SolitaireGameLogic {
    return this.gameLogic;
  }
  public set onBoardChangedAction(value: OnBoardChangedAction) {
    this.gameLogic.onBoardChangedAction = value;
  }
  public set onCommandCommittedAction(value: OnCommandCommittedAction) {
    this.gameLogic.onCommandCommittedAction = value;
  }
  public set onScoreChangedAction(value: OnScoreChangedAction) {
    this.gameLogic.onScoreChangedAction = value;
  }
  public set onGameStateChangedAction(value: OnGameStateChangedAction) {
    this.gameStateChangedAction = value;
  }

  private gameState: GameState;
  private sideEffectsSubmitted: boolean;
  private gameStateChangedAction?: OnGameStateChangedAction;

  constructor(
    private gameLogic: SolitaireGameLogic,
    public readonly roundResultsSubmitter: RoundResultSubmitter,
  ) {
    this.gameState = GameState.Init;
    this.sideEffectsSubmitted = false;
  }

  public async submitSideEffectsIfNeeded(): Promise<void> {
    if (this.sideEffectsSubmitted) {
      return;
    }

    await this.roundResultsSubmitter.submitResult(this.gameLogic, true);

    this.sideEffectsSubmitted = true;
  }

  public isFinished(): boolean {
    return this.sideEffectsSubmitted;
  }

  public onDealingAnimationFinished() {
    this.setGameState(GameState.InProgress);
  }

  public onCardPlaced(_card: Card) {
    if (this.gameState !== GameState.InProgress) {
      return;
    }

    if (this.gameLogic.isGameWon) {
      this.onGameWon();
    }
  }

  public onDeckClicked() {
    if (this.gameState === GameState.InProgress) {
      this.gameLogic.drawFromDeck();
    }
  }

  public autoMoveCard(card: Card): boolean {
    if (this.gameState === GameState.InProgress) {
      return this.gameLogic.autoMoveCard(card);
    }
    return false;
  }

  public autoFinishNextCard(): boolean {
    if (this.gameState === GameState.AutoFinish) {
      return this.gameLogic.autoFinishNextCard();
    }
    return false;
  }

  public undoLastCommand() {
    if (this.gameState === GameState.InProgress) {
      this.gameLogic.execute(new UndoCommand());
    }
  }

  public allPossibleHints(): PossibleMove[] {
    if (this.gameState === GameState.InProgress) {
      return this.gameLogic.getHintedMoves();
    }
    return [];
  }

  public onAutoFinishCompleted() {
    if (this.gameState !== GameState.AutoFinish) {
      return;
    }
    this.setGameState(GameState.EndingAnimation);
  }

  public onEndingAnimationCompleted() {
    if (this.gameState !== GameState.EndingAnimation) {
      return;
    }
    this.setGameState(GameState.Finished);
  }

  private onGameWon() {
    this.setGameState(GameState.AutoFinish);
  }

  private setGameState(gameState: GameState) {
    this.gameState = gameState;
    this.gameStateChangedAction?.(gameState);

    switch (gameState) {
      case GameState.EndingAnimation:
        void this.submitSideEffectsIfNeeded();
        break;
    }
  }
}
